import { ContextHolder } from "@frontegg/react";
import {
  AUTHORIZE_TIMEOUT_MS,
  TOKEN_EXPIRY_BUFFER_MS
} from "./tokenRefresh.consts";

// The token is a user-scoped identity proof — the server authorizes the requested
// account per call, so the only thing this module cares about is expiry.
let requestAuthorize = null;
let refreshPromise = null;
let decodedToken = null;
let decodedPayload = null;

export const registerTokenRefresh = (authorizeFn) => {
  requestAuthorize = authorizeFn;
};

export const getLiveAccessToken = () => {
  try {
    return ContextHolder.default().getAccessToken();
  } catch {
    return null;
  }
};

const getTokenPayload = (token) => {
  if (token !== decodedToken) {
    decodedToken = token;
    try {
      decodedPayload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    } catch {
      decodedPayload = null;
    }
  }
  return decodedPayload;
};

// A token without a readable expiry is assumed valid — the server stays the authority
// and the 401-retry layer still covers it.
export const isTokenValid = (token) => {
  if (!token) {
    return false;
  }
  const expirySeconds = getTokenPayload(token)?.exp;
  if (!expirySeconds) {
    return true;
  }
  return (expirySeconds * 1000) - TOKEN_EXPIRY_BUFFER_MS > Date.now();
};

// Bounded so a hung Frontegg call can't leave every outgoing request awaiting forever;
// authorize failures resolve too — callers proceed with the current token and the
// request fails naturally into the 401-retry layer.
const authorizeWithTimeout = () => {
  let timeoutId = null;
  return Promise.race([
    Promise.resolve(requestAuthorize())
      .catch((error) => console.error("Frontegg authorize failed during token refresh:", error)),
    new Promise((resolve) => {
      timeoutId = setTimeout(resolve, AUTHORIZE_TIMEOUT_MS);
    })
  ]).finally(() => clearTimeout(timeoutId));
};

const performRefresh = async (failedToken) => {
  const liveToken = getLiveAccessToken();
  if (liveToken && liveToken !== failedToken && isTokenValid(liveToken)) {
    return liveToken;
  }
  if (!requestAuthorize) {
    return null;
  }
  await authorizeWithTimeout();
  return getLiveAccessToken();
};

export const refreshAccessToken = ({ failedToken } = {}) => {
  if (!refreshPromise) {
    refreshPromise = performRefresh(failedToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Pre-flight gate for outgoing requests: hands back the current token when it is
// usable, otherwise refreshes before the request leaves.
export const getValidAccessToken = async () => {
  const liveToken = getLiveAccessToken();
  if (isTokenValid(liveToken)) {
    return liveToken;
  }

  try {
    const refreshedToken = await refreshAccessToken({ failedToken: liveToken });
    return refreshedToken || liveToken;
  } catch (error) {
    console.error("Failed to refresh access token before request:", error);
    return liveToken;
  }
};
